import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UsuarioLogin } from '../interfaces/usuario-login';
import { environment } from '../../environments/environment';
import { EmpresaConfig } from '../interfaces/empresa-config';
import { Observable, BehaviorSubject } from 'rxjs';
import { SessionStorageService } from '../utils/services/session-storage.service';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private configURL = `${environment.s3_url}/EMPRESA/${this.empresaHostname}/config-portal.json`;

  empresaConfig$ = new BehaviorSubject<EmpresaConfig>(null);

  constructor(
    private httpClient: HttpClient,
    private _serviceStorage: SessionStorageService,
    private _title: Title
  ) {
    this.subscribeEmpresaConfig();
  }

  /**
   * save user;
   * @param user
   */
  login(usuario: UsuarioLogin): Promise<UsuarioLogin | undefined> {
    return this.httpClient.post<UsuarioLogin>(`${environment.apiURL}/auth/login`, usuario).toPromise();
  }

  /**
   *
   * @param user
   */
  getUserByEmail(email: string) {
    return this.httpClient.get<UsuarioLogin>(`${environment.apiURL}/api/administration/v1/usuarios/email?email=${email}`);
  }

  /**
   * Cadastra um novo usuário;
   * @param usuario
   * @returns
   */
  novo(usuario: UsuarioLogin) {
    return this.httpClient.post<UsuarioLogin>(`${environment.apiURL}/auth/register`, usuario);
  }

  /**
   * Atualiza a senha do usuário;
   * @param usuario
   * @returns
   */
  atualizaSenha(usuario: UsuarioLogin) {
    const params = new HttpParams().set('parceiroNegocioId', usuario.parceiroNegocio.parceiroNegocioId).set('password', usuario.password);

    return this.httpClient.put<boolean>(`${environment.apiURL}/auth/atualiza-senha/${usuario.portalClienteLoginId}`, null, { params });
  }

  enviaNovaSenhaPorEmail(email: string) {
    const params = new HttpParams().set('email', email);

    return this.httpClient.put<boolean>(`${environment.apiURL}/auth/envia-nova-senha-por-email`, null, { params });
  }

  get empresaHostname() {
    return window.location.hostname.replace('qaportal.', 'qamovez.').replace('portal.', 'movez.');
  }

  get configPadrao() {
    return {
      backgroundURL: '/assets/images/bg/logz.png',
      logoURL: './assets/images/logo/logz.png',
      nomeEmpresa: 'LOGZ - Movez - Portal Cliente',
      icon: '../assets/images/faviconlogz.ico',
    } as EmpresaConfig;
  }

  private isCacheValido(cacheConfig: string) {
    const configSession = JSON.parse(cacheConfig) as { data: number; config: EmpresaConfig };
    const CACHE_DURACAO = 48 * 60 * 60 * 1000; // 48 horas em ms
    const now = Date.now();

    return configSession.data && now - configSession.data < CACHE_DURACAO;
  }

  private setConfigPadroes(config: EmpresaConfig) {
    config.backgroundURL = config.backgroundURL ?? this.configPadrao.backgroundURL;
    config.logoURL = config.logoURL ?? this.configPadrao.logoURL;
    config.nomeEmpresa = config.nomeEmpresa ?? this.configPadrao.nomeEmpresa;
    config.icon = config.icon ?? this.configPadrao.icon;

    return config;
  }

  loadEmpresaConfig() {
    return new Observable<EmpresaConfig>(subs => {
      const empresaConfigStorage = this._serviceStorage.getItem('config');

      if (empresaConfigStorage && this.isCacheValido(empresaConfigStorage)) {
        const configSession = JSON.parse(empresaConfigStorage) as { data: number; config: EmpresaConfig };
        configSession.config = this.setConfigPadroes(configSession.config);
        this.empresaConfig$.next(configSession.config);
        subs.next(configSession.config);
      } else {
        fetch(this.configURL, { method: 'GET' })
          .then(res => (res.status === 200 ? res.json() : undefined))
          .then(body => {
            if (!body) {
              body = this.configPadrao;
            } else {
              body = this.setConfigPadroes(body);
            }

            this._serviceStorage.setItem('config', JSON.stringify({ data: Date.now(), config: body }));
            this.empresaConfig$.next(body);
            subs.next(body);
          });
      }
    });
  }

  loadFaviconETitulo(config: EmpresaConfig) {
    this._title.setTitle(config.nomeEmpresa);
    const icon = document.querySelector("head link[rel=icon][type='image/x-icon']") as HTMLLinkElement;
    icon.href = config.icon;
  }

  private subscribeEmpresaConfig() {
    this.empresaConfig$.subscribe(config => {
      if (!config) {
        const sessionConfig = this._serviceStorage.getItem('config');
        if (sessionConfig) {
          config = (JSON.parse(sessionConfig) as { data: number; config: EmpresaConfig }).config;
        }
      }

      if (config) {
        this.loadFaviconETitulo(config);
      }
    });
  }
}
